import React, { useState, useEffect } from 'react'
import { supabase } from '../../supabaseClient'
import Swal from 'sweetalert2'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'

const bgImage   = "https://hbylyyfhigscnrozlxqe.supabase.co/storage/v1/object/public/Background/Login_BG.png"  
const logoImage = "https://hbylyyfhigscnrozlxqe.supabase.co/storage/v1/object/public/Background/CG%20LOGO.png"   

export default function StaffSetup() {
  useDocumentTitle('Staff Setup')

  const [step, setStep] = useState(1) 
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [otp, setOtp] = useState(["", "", "", "", "", ""])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [staffEmail, setStaffEmail] = useState("")
  const [companyEmail, setCompanyEmail] = useState("")
  const [resendLoading, setResendLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    upperCase: false,
    number: false,
    special: false
  })

  useEffect(() => {
    console.log('=== STAFF SETUP PAGE LOADED ===')
    
    const email = sessionStorage.getItem('userEmail')
    const userType = sessionStorage.getItem('userType')
    const isAuthenticated = sessionStorage.getItem('isAuthenticated')
    const mustChangePassword = sessionStorage.getItem('mustChangePassword')
    
    console.log('Session check:', {
        email,
        userType,
        isAuthenticated,
        mustChangePassword,
        mustChangePasswordType: typeof mustChangePassword
    })
    
    if (!email || !isAuthenticated || userType !== 'staff') {
        console.log('REDIRECTING TO LOGIN - Missing credentials')
        window.location.href = '/login'
        return
    }
    
    if (mustChangePassword !== 'true') {
        console.log('REDIRECTING TO DASHBOARD - Password already changed')
        window.location.href = '/staff-dashboard'
        return
    }
    
    console.log('All checks passed, staying on StaffSetup page')
    setStaffEmail(email)

    // The login email is company_email; the real Gmail inbox is the `email` column
    supabase
      .from('staff_setup_lookup')
      .select('email')
      .eq('company_email', email)
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error fetching gmail address:', error)
          return
        }
        if (data?.email) {
          setCompanyEmail(data.email)
        }
      })
  }, [])
  useEffect(() => {
    if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
        return () => clearTimeout(timer)
    }
  }, [countdown])

  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = `
      .swal2-confirm {
        background-color: #A0826D !important;
        border: none !important;
      }
      .swal2-confirm:hover {
        background-color: #8B5A3C !important;
      }
      .swal2-cancel {
        background-color: #6b7280 !important;
        border: none !important;
      }
      .swal2-cancel:hover {
        background-color: #5a5a5a !important;
      }
    `
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 8,
      upperCase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    }
    setPasswordValidation(validation)
    return validation.length && validation.upperCase && validation.number && validation.special
  }

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus()
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6)
    if (!/^\d+$/.test(pastedData)) return
    
    const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6)
    setOtp(newOtp)

    const lastIndex = Math.min(pastedData.length - 1, 5)
    document.getElementById(`otp-${lastIndex}`)?.focus()
  }

  const handleSetupPassword = async () => {
    setError("")
    setLoading(true)

    if (!newPassword || !confirmPassword) {
      await Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please fill in all required fields.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#FF6F3C'
      })
      setLoading(false)
      return
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match")
      setLoading(false)
      return
    }

    if (!validatePassword(newPassword)) {
      setError("Password must meet all requirements")
      setLoading(false)
      return
    }

    try {
      const { data, error: setupError } = await supabase
        .rpc('staff_setup_password', {
          p_email: staffEmail,
          p_new_password: newPassword
        })

      if (setupError) throw setupError

      if (data && data.length > 0 && data[0].success) {
        const otpCode = data[0].message
        
        console.log('OTP generated:', otpCode)
        
        try {
          const { data: emailData, error: emailError } = await supabase.functions.invoke(
             'send-otp-email-v2',
            {
              body: {
                email: companyEmail || staffEmail,
                otp: otpCode
              }
            }
          )
          if (emailError) {
            console.error('Email error:', emailError)
            console.warn('Email sending failed, but password was updated. OTP:', otpCode)
          } else {
            console.log('Email sent successfully:', emailData)
          }
        } catch (emailErr) {
          console.error('Email sending exception:', emailErr)
        }

        await Swal.fire({
          icon: 'success',
          title: 'Password Set!',
          html: `
            <p>A verification code has been sent to your email.</p>
            <p class="text-xs text-gray-500 mt-2">Check your spam folder if you don't see it.</p>
          `,
          confirmButtonText: 'OK',
          confirmButtonColor: '#FF6F3C'
        })

        setStep(2)
        setCountdown(60)
      } else {
        throw new Error(data[0]?.message || 'Failed to setup password')
      }
    } catch (err) {
      console.error('Password setup error:', err)
      setError('An error occurred during password setup')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    setError("")
    const otpCode = otp.join("")

    if (otpCode.length !== 6) {
      setError("Please enter the complete 6-digit code")
      return
    }

    setLoading(true)

    try {
      const { data, error: verifyError } = await supabase
        .rpc('verify_staff_setup_otp', {
          p_email: staffEmail,
          p_otp: otpCode
        })

      if (verifyError) throw verifyError

      if (data && data.length > 0 && data[0].success) {
        await Swal.fire({
          icon: 'success',
          title: 'Setup Complete!',
          text: 'Your account has been verified. Please login with your new password.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#FF6F3C'
        })

        const secretCode = sessionStorage.getItem('secretCodeEntered')
        const captchaVerified = sessionStorage.getItem('captchaVerified')
        const captchaTimestamp = sessionStorage.getItem('captchaTimestamp')
        
        sessionStorage.clear()

        if (secretCode) sessionStorage.setItem('secretCodeEntered', secretCode)
        if (captchaVerified) sessionStorage.setItem('captchaVerified', captchaVerified)
        if (captchaTimestamp) sessionStorage.setItem('captchaTimestamp', captchaTimestamp)

        window.location.href = '/login'
      } else {
        const message = data?.[0]?.message || 'Invalid verification code'
        
        if (message.includes('expired')) {
          await Swal.fire({
            icon: 'warning',
            title: 'Code Expired',
            text: 'Your verification code has expired. Please go back and set your password again.',
            confirmButtonText: 'Go Back',
            confirmButtonColor: '#FF6F3C'
          })
          setStep(1)
          setOtp(["", "", "", "", "", ""])
          setNewPassword("")
          setConfirmPassword("")
          setError("")
        } else if (message.includes('No OTP found')) {
          await Swal.fire({
            icon: 'warning',
            title: 'No Code Found',
            text: 'No verification code found. Please go back and set your password again.',
            confirmButtonText: 'Go Back',
            confirmButtonColor: '#FF6F3C'
          })
          setStep(1)
          setOtp(["", "", "", "", "", ""])
          setNewPassword("")
          setConfirmPassword("")
          setError("")
        } else {
          setError('Invalid verification code. Please try again.')
          setOtp(["", "", "", "", "", ""])
          document.getElementById('otp-0')?.focus()
        }
      }
    } catch (err) {
      console.error('OTP verification error:', err)
      setError('Failed to verify code. Please try again.')
      setOtp(["", "", "", "", "", ""])
      document.getElementById('otp-0')?.focus()
    } finally {
      setLoading(false)
    }
  }

  const handleResendOtp = async () => {
    if (countdown > 0) return
    
    setResendLoading(true)
    setError("")
    
    try {
      const { data, error } = await supabase
        .rpc('resend_staff_setup_otp', {
          p_email: staffEmail
        })

      if (error) throw error

      if (data && data.length > 0 && data[0].success) {
        const otpCode = data[0].otp
        
        console.log('New OTP generated:', otpCode)
        
        try {
        await supabase.functions.invoke('send-otp-email-v2', {
            body: {
              email: companyEmail || staffEmail,
              otp: otpCode
            }
          })
        } catch (emailErr) {
          console.error('Email sending error:', emailErr)
        }

        await Swal.fire({
          icon: 'success',
          title: 'Code Resent!',
          text: 'A new verification code has been sent to your email.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#FF6F3C',
          timer: 2000
        })
        
        setCountdown(60)
        setOtp(["", "", "", "", "", ""])
        document.getElementById('otp-0')?.focus()
      }
    } catch (err) {
      console.error('Resend OTP error:', err)
      setError('Failed to resend code')
    } finally {
      setResendLoading(false)
    }
  }

  const handleBackToForm = () => {
    setStep(1)
    setOtp(["", "", "", "", "", ""])
    setError('')
  }

  const handleLogout = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Cancel Setup?',
      text: 'Are you sure you want to cancel? You will need to complete the setup later.',
      showCancelButton: true,
      confirmButtonText: 'Yes, Cancel',
      cancelButtonText: 'Stay Here',
      confirmButtonColor: '#FF6F3C',
      cancelButtonColor: '#6B7280'
    })

    if (result.isConfirmed) {
      sessionStorage.clear()
      localStorage.clear()
      window.location.href = '/login'
    }
  }

  return (
    <div
      className="relative min-h-screen flex items-center justify-center p-4"      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >

      <div className="relative bg-[#1a1a1a] rounded-2xl shadow-2xl p-5 sm:p-8 w-full max-w-3xl"         style={{ background: 'rgba(30, 20, 15, 0.85)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {step === 1 ? (
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
            {/* Left: Logo */}
            <div className="flex flex-col items-center justify-center w-full sm:w-[200px] shrink-0">
              <img src={logoImage} alt="Coffee Gus·To" className="w-40 object-contain" />
            </div>

            {/* Right: Form */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-white mb-1">First Time Setup</h1>
              <p className="text-sm text-gray-400 mb-5">Please set your personal admin credentials</p>

              {error && (
                <div className="bg-red-900 border border-red-600 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {/* Current Email (read-only) */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">Current Email Address</label>
                  <input
                    type="text"
                    value={staffEmail}
                    disabled
                    className="w-full px-3 py-2.5 bg-[#fff] border border-[#333] rounded-lg text-gray-400 text-sm cursor-not-allowed"
                  />
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value)
                        validatePassword(e.target.value)
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleSetupPassword()}
                      placeholder="Enter your New Password"
                      className="w-full px-3 pr-10 py-2.5 bg-[#fff] border border-[#333] rounded-lg text-black text-sm placeholder-gray-600 focus:ring-2 focus:ring-[#FF6F3C] focus:border-transparent outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showNewPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"><path d="M15 12a3 3 0 1 1-6 0a3 3 0 0 1 6 0"/><path d="M2 12c1.6-4.097 5.336-7 10-7s8.4 2.903 10 7c-1.6 4.097-5.336 7-10 7s-8.4-2.903-10-7"/></g></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a13.4 13.4 0 0 0 3 2.685M21 10a13.4 13.4 0 0 1-3 2.685m-8 1.624L9.5 16.5m.5-2.19a10.6 10.6 0 0 0 4 0m-4 0a11.3 11.3 0 0 1-4-1.625m8 1.624l.5 2.191m-.5-2.19a11.3 11.3 0 0 0 4-1.625m0 0l1.5 1.815M6 12.685L4.5 14.5"/></svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">Confirm New Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSetupPassword()}
                      placeholder="Confirm your New Password"
                      className={`w-full px-3 pr-10 py-2.5 bg-[#fff] border rounded-lg text-black text-sm placeholder-gray-600 focus:ring-2 focus:border-transparent outline-none ${
                        confirmPassword && confirmPassword === newPassword
                          ? 'border-green-500 focus:ring-green-500'
                          : confirmPassword && confirmPassword !== newPassword
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-[#333] focus:ring-[#FF6F3C]'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showConfirmPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"><path d="M15 12a3 3 0 1 1-6 0a3 3 0 0 1 6 0"/><path d="M2 12c1.6-4.097 5.336-7 10-7s8.4 2.903 10 7c-1.6 4.097-5.336 7-10 7s-8.4-2.903-10-7"/></g></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a13.4 13.4 0 0 0 3 2.685M21 10a13.4 13.4 0 0 1-3 2.685m-8 1.624L9.5 16.5m.5-2.19a10.6 10.6 0 0 0 4 0m-4 0a11.3 11.3 0 0 1-4-1.625m8 1.624l.5 2.191m-.5-2.19a11.3 11.3 0 0 0 4-1.625m0 0l1.5 1.815M6 12.685L4.5 14.5"/></svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember Me + Forgot Password */}
                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input type="checkbox" className="accent-[#FF6F3C]" />
                    Remember Me
                  </label>
                  <button type="button" className="text-sm text-gray-400 hover:text-white transition">
                    Forgot Password?
                  </button>
                </div>

                {/* Password validation */}
                {newPassword && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-1">                    {[
                      { key: 'length', label: '8+ chars' },
                      { key: 'upperCase', label: 'Uppercase' },
                      { key: 'number', label: 'Number' },
                      { key: 'special', label: 'Special' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-1 text-xs">
                        {passwordValidation[key] ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" className="text-green-500 flex-shrink-0"><path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10s10-4.5 10-10S17.5 2 12 2m-2 15l-5-5l1.41-1.41L10 14.17l7.59-7.59L19 8z"/></svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" className="text-gray-600 flex-shrink-0"><path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10s10-4.5 10-10S17.5 2 12 2"/></svg>
                        )}
                        <span className={passwordValidation[key] ? 'text-green-500' : 'text-gray-600'}>{label}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* CTA Buttons */}
                <button
                  onClick={handleSetupPassword}
                  disabled={loading}
                  className="w-full bg-[#FF6F3C] hover:bg-[#e85a28] text-white font-semibold py-2.5 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {loading ? 'Setting Up...' : 'Complete Setup'}
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full border border-[#444] text-gray-300 hover:bg-[#2a2a2a] font-semibold py-2.5 rounded-lg transition duration-200 text-sm"
                >
                  Cancel
                </button>
              </div>

              {/* Footer note */}
              <div className="mt-4 flex items-start gap-2 text-xs text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" className="flex-shrink-0 mt-0.5"><path fill="currentColor" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10s-4.477 10-10 10m0-2a8 8 0 1 0 0-16a8 8 0 0 0 0 16M11 7h2v2h-2zm0 4h2v6h-2z"/></svg>
                <p>This is a secure admin area. Only authorized personnel with valid credentials can access this system. All login attempts are logged and monitored.</p>
              </div>
              <p className="text-center text-xs text-gray-600 mt-2">
                Need access? Contact your system administrator
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }} className='space-y-6'>
            <div>
              <label className='block text-sm font-semibold text-white mb-3 text-center'>
                Enter Verification Code
              </label>
              <div className='flex gap-1.5 sm:gap-2 justify-center'>                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength='1'
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    autoComplete='off'
                    autoCorrect='off'
                    autoCapitalize='off'
                    spellCheck='false'
                    className='w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A0826D] focus:border-[#A0826D] outline-none transition'                    required
                  />
                ))}
              </div>
            </div>

            <button
              type='submit'
              disabled={loading || otp.join('').length !== 6}
              className='w-full bg-[#FF6F3C] hover:bg-[#ff4c0b] text-white font-semibold py-3 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {loading ? 'Verifying...' : 'Verify & Complete Setup'}
            </button>

            <button
              type='button'
              onClick={handleBackToForm}
              className='w-full border-2 border-[#FF6F3C] text-[#ff4c0b] hover:bg-[#C9B5A0] hover:bg-opacity-20 font-semibold py-3 rounded-lg transition duration-200'
            >
              Back to Form
            </button>

            <div className='text-center'>
              <p className='text-sm text-gray-600 mb-2'>Didn't receive the code?</p>
              <button
                type='button'
                onClick={handleResendOtp}
                disabled={countdown > 0 || resendLoading}
                className='text-[#FF6F3C] hover:text-[#ff4c0b] font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {resendLoading ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
              </button>
            </div>
          </form>
        )}

        <div className='mt-6 flex items-start gap-2 text-xs text-gray-500'>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="flex-shrink-0 mt-0.5">
            <path fill="currentColor" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10s-4.477 10-10 10m0-2a8 8 0 1 0 0-16a8 8 0 0 0 0 16M11 7h2v2h-2zm0 4h2v6h-2z"/>
          </svg>
          <p>
            {step === 1 
              ? 'You must set up a new password before accessing the staff dashboard. Make sure to choose a strong, unique password.'
              : 'This code will expire in 10 minutes. Check your spam folder if you don\'t see the email.'}
          </p>
        </div>
      </div>
    </div>
  )
}